<template>
  <div class="relative lg:mt-8 pb-8 lg:pb-10">
    <div
      ref="stickyAnchorNavRef"
      class="hidden lg:block navigation-bar sticky top-0 left-0 z-10"
    >
      <ul
        class="list-none relative flex items-start w-full bg-EXPONDO-white gap-x-12 text-EXPONDO-grey-60"
      >
        <ProductAnchorNavItem
          v-for="item in anchorNavItems"
          :key="item.sectionId"
          :item="item"
        />
      </ul>
    </div>
    <slot />
  </div>
</template>

<script lang="ts">
import { useResizeObserver } from '@vueuse/core';
import { PDP_NAV_ANCHORS } from '~~/constants/pdpNavAnchors';
import ProductAnchorNavItem from '~/components/atoms/Product/ProductAnchorNavItem.vue';

export default defineComponent({
  name: 'ProductAnchorNav',
  components: {
    ProductAnchorNavItem,
  },
  props: {
    shouldShowDescription: {
      type: Boolean,
      required: true,
    },
    shouldShowFaq: {
      type: Boolean,
      required: true,
    },
    shouldShowRelatedAccessories: {
      type: Boolean,
      required: true,
    },
    shouldShowParentsForAccessory: {
      type: Boolean,
      required: true,
    },
    shouldShowAboutBrand: {
      type: Boolean,
      required: true,
    },
    shouldShowSiblingCategories: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {

    const i18n = useI18n();
    const route = useRoute();
    const { setStickyAnchorNavHeight } = useAnchorNavigationStore();

    const stickyAnchorNavRef = ref(null);
    const { slug } = route.params;
    const { loaded: productDataLoaded } = useProductExtended(slug);

    useResizeObserver(stickyAnchorNavRef, (entries) => {

      const entry = entries[0];

      if (entry) {

        setStickyAnchorNavHeight(entry.contentRect.height);
      
      }
    
    });

    const anchorNavItems = computed(() => [
      {
        condition: props.shouldShowSiblingCategories,
        sectionId: PDP_NAV_ANCHORS.SIBLING_CATEGORIES,
        text: i18n.t('Similar Categories'),
      },
      {
        condition: productDataLoaded.value,
        sectionId: PDP_NAV_ANCHORS.TECHNICAL_DETAILS,
        text: i18n.t('Technical details'),
      },
      {
        condition: props.shouldShowDescription,
        sectionId: PDP_NAV_ANCHORS.PRODUCT_DESCRIPTION,
        text: i18n.t('Product Description'),
      },
      {
        condition: props.shouldShowRelatedAccessories,
        sectionId: PDP_NAV_ANCHORS.RELATED_ACCESSORIES,
        text: i18n.t('Accessories'),
      },
      {
        condition: props.shouldShowParentsForAccessory,
        sectionId: PDP_NAV_ANCHORS.PARENTS_FOR_ACCESSORY,
        text: i18n.t('Matches to'),
      },
      {
        condition: props.shouldShowAboutBrand,
        sectionId: PDP_NAV_ANCHORS.ABOUT_BRAND,
        text: i18n.t('About the brand'),
      },
      {
        condition: true,
        sectionId: PDP_NAV_ANCHORS.RATING_REVIEWS,
        text: i18n.t('Ratings & reviews'),
      },
      {
        condition: props.shouldShowFaq,
        sectionId: PDP_NAV_ANCHORS.FAQ,
        text: i18n.t('Frequently asked questions'),
      },
    ]);

    onMounted(() => {

      if (stickyAnchorNavRef.value) {

        setStickyAnchorNavHeight(stickyAnchorNavRef.value?.offsetHeight || 0);
      
      }
    
    });

    return {
      PDP_NAV_ANCHORS,
      anchorNavItems,
      stickyAnchorNavRef, // Make sure to return the ref
    };
  
  },
});
</script>

<style lang="scss" scoped>
ul {
  &::before {
    content: '';

    @apply absolute shadow-bottom -bottom-0.5 left-1/2 transform -translate-x-1/2 w-screen h-1;
  }
}
</style>
